import { FC, MouseEvent, useCallback, useMemo } from 'react';

import { MdKeyboardArrowLeft, MdKeyboardArrowRight } from 'react-icons/md';

import { PromptTypesEnum } from '../../enums/prompt-types-enum';
import { useInfiniteCarousel } from '../../hooks/use-infinite-carousel';
import {
  resolvePromptMediaItems,
  TPromptMediaValue,
} from '../../utils/normalize-prompt-media';
import {
  CarouselImage,
  CarouselIndicator,
  CarouselIndicators,
  CarouselNavigationButton,
  CarouselRoot,
  CarouselSlide,
  CarouselTrack,
  CarouselVideo,
} from './styles';

interface IPromptMediaCarouselProps {
  mediaUrls: TPromptMediaValue;
  compactMediaUrls?: TPromptMediaValue;
  type?: PromptTypesEnum;
  alt: string;
  className?: string;
  fallbackToDefault?: boolean;
  imageLoading?: 'eager' | 'lazy';
  objectFit?: 'cover' | 'contain';
  showIndicators?: boolean;
  showNavigationControls?: boolean;
  hoverScale?: number;
  isActive?: boolean;
  playVideos?: boolean;
}

export const PromptMediaCarousel: FC<IPromptMediaCarouselProps> = ({
  mediaUrls,
  compactMediaUrls,
  type,
  alt,
  className,
  fallbackToDefault = false,
  imageLoading = 'lazy',
  objectFit = 'cover',
  showIndicators = false,
  showNavigationControls = false,
  hoverScale = 1,
  isActive = true,
  playVideos = true,
}) => {
  const slides = useMemo(
    () =>
      resolvePromptMediaItems(
        mediaUrls,
        compactMediaUrls,
        type,
        fallbackToDefault
      ),
    [compactMediaUrls, fallbackToDefault, mediaUrls, type]
  );

  const effectiveSlides = useMemo(() => {
    if (isActive) {
      return slides;
    }

    return slides.slice(0, 1);
  }, [isActive, slides]);

  const {
    activeIndex,
    goToNext,
    goToPrevious,
    isTransitioning,
    renderIndex,
    transitionEnabled,
    handleTransitionEnd,
    hasMultipleItems,
  } = useInfiniteCarousel(effectiveSlides.length);

  const renderedSlides = useMemo(() => {
    if (!effectiveSlides.length) {
      return [];
    }

    return hasMultipleItems
      ? [
          effectiveSlides[effectiveSlides.length - 1],
          ...effectiveSlides,
          effectiveSlides[0],
        ]
      : effectiveSlides;
  }, [effectiveSlides, hasMultipleItems]);

  const handlePreviousClick = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
      event.stopPropagation();
      goToPrevious();
    },
    [goToPrevious]
  );

  const handleNextClick = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
      event.stopPropagation();
      goToNext();
    },
    [goToNext]
  );

  if (!effectiveSlides.length) {
    return null;
  }

  return (
    <CarouselRoot className={className} $hoverScale={hoverScale}>
      <CarouselTrack
        $renderIndex={renderIndex}
        $transitionEnabled={transitionEnabled}
        onTransitionEnd={handleTransitionEnd}
      >
        {renderedSlides.map((slide, index) => (
          <CarouselSlide key={`${slide.originalUrl}-${index}`}>
            {slide.isVideo ? (
              <CarouselVideo
                src={slide.originalUrl}
                autoPlay={playVideos}
                loop={playVideos}
                muted
                playsInline
                poster={slide.posterUrl}
                preload="metadata"
                $objectFit={objectFit}
              />
            ) : (
              <CarouselImage
                src={slide.displayUrl}
                alt={alt}
                loading={imageLoading}
                $objectFit={objectFit}
              />
            )}
          </CarouselSlide>
        ))}
      </CarouselTrack>

      {showNavigationControls && hasMultipleItems && effectiveSlides.length > 1 && (
        <>
          <CarouselNavigationButton
            type="button"
            aria-label="Midia anterior"
            onClick={handlePreviousClick}
            disabled={isTransitioning}
            $left
          >
            <MdKeyboardArrowLeft size={24} />
          </CarouselNavigationButton>

          <CarouselNavigationButton
            type="button"
            aria-label="Proxima midia"
            onClick={handleNextClick}
            disabled={isTransitioning}
            $right
          >
            <MdKeyboardArrowRight size={24} />
          </CarouselNavigationButton>
        </>
      )}

      {showIndicators && hasMultipleItems && effectiveSlides.length > 1 && (
        <CarouselIndicators>
          {effectiveSlides.map((_, index) => (
            <CarouselIndicator
              key={String(index)}
              $active={index === activeIndex}
            />
          ))}
        </CarouselIndicators>
      )}
    </CarouselRoot>
  );
};
